import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities, ensureCurrentUser } from '../../util/data';
import { v4 as uuidv4 } from 'uuid';
import { UploadImage, UploadVideoChunked, DeleteMedia } from '../../util/cloudinary';
import { generateGalleryImageSeoTags, updateCloudinaryContext } from '../../util/api';
import {
    MAX_FILES_PER_UPLOAD,
    MAX_UPLOAD_SIZE_PER_BATCH,
    MAX_FILE_SIZE,
    MAX_STORE_STORAGE,
    ACCEPTED_PHOTO_TYPES,
    ACCEPTED_VIDEO_TYPES,
    formatBytes,
} from './constants';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/MediaLibraryPage/SET_INITIAL_STATE';

export const LOAD_MEDIA = 'app/MediaLibraryPage/LOAD_MEDIA';
export const ADD_MEDIA = 'app/MediaLibraryPage/ADD_MEDIA';
export const DELETE_MEDIA = 'app/MediaLibraryPage/DELETE_MEDIA';
export const REORDER_MEDIA = 'app/MediaLibraryPage/REORDER_MEDIA';
export const SET_FILTER = 'app/MediaLibraryPage/SET_FILTER';
export const SET_BULK_SELECTION_MODE = 'app/MediaLibraryPage/SET_BULK_SELECTION_MODE';
export const TOGGLE_MEDIA_SELECTION = 'app/MediaLibraryPage/TOGGLE_MEDIA_SELECTION';
export const SELECT_ALL_MEDIA = 'app/MediaLibraryPage/SELECT_ALL_MEDIA';
export const UNSELECT_ALL_MEDIA = 'app/MediaLibraryPage/UNSELECT_ALL_MEDIA';
export const SET_GALLERY_VIEW = 'app/MediaLibraryPage/SET_GALLERY_VIEW';

export const SAVE_REQUEST = 'app/MediaLibraryPage/SAVE_REQUEST';
export const SAVE_SUCCESS = 'app/MediaLibraryPage/SAVE_SUCCESS';
export const SAVE_ERROR = 'app/MediaLibraryPage/SAVE_ERROR';
export const LOAD_REQUEST = 'app/MediaLibraryPage/LOAD_REQUEST';
export const LOAD_SUCCESS = 'app/MediaLibraryPage/LOAD_SUCCESS';
export const LOAD_ERROR = 'app/MediaLibraryPage/LOAD_ERROR';

// New action types for upload warnings/failures
export const SET_UPLOAD_WARNING = 'app/MediaLibraryPage/SET_UPLOAD_WARNING';
export const CLEAR_UPLOAD_WARNING = 'app/MediaLibraryPage/CLEAR_UPLOAD_WARNING';
export const SET_UPLOAD_FAILURES = 'app/MediaLibraryPage/SET_UPLOAD_FAILURES';
export const CLEAR_UPLOAD_FAILURES = 'app/MediaLibraryPage/CLEAR_UPLOAD_FAILURES';
export const SET_STORAGE_USED = 'app/MediaLibraryPage/SET_STORAGE_USED';
export const UPDATE_UPLOAD_PROGRESS = 'app/MediaLibraryPage/UPDATE_UPLOAD_PROGRESS';
export const CLEAR_UPLOAD_PROGRESS = 'app/MediaLibraryPage/CLEAR_UPLOAD_PROGRESS';

// Upload lifecycle action types
export const UPLOAD_START = 'app/MediaLibraryPage/UPLOAD_START';
export const UPLOAD_SUCCESS = 'app/MediaLibraryPage/UPLOAD_SUCCESS';
export const UPLOAD_ERROR = 'app/MediaLibraryPage/UPLOAD_ERROR';
export const UPDATE_MEDIA_ITEM = 'app/MediaLibraryPage/UPDATE_MEDIA_ITEM';
export const DISMISS_FAILED_UPLOAD = 'app/MediaLibraryPage/DISMISS_FAILED_UPLOAD';

// ================ Reducer ================ //

const initialState = {
    media: [],
    filter: 'all', // 'all', 'photos', 'videos'
    bulkSelectionMode: false,
    selectedMediaIds: [],
    galleryViewIndex: null, // null or index of media item being viewed
    saveInProgress: false,
    saveError: null,
    loadInProgress: false,
    loadError: null,
    hasUnsavedChanges: false,
    // Upload limits & failures
    uploadWarning: null, // { type: 'file_count' | 'batch_size' | 'quota_exceeded', message: string, skippedFiles: string[] }
    uploadFailures: [], // [{ fileName, error }]
    storageUsed: 0, // bytes used by current store media
    // Upload progress tracking (for chunked uploads)
    uploadProgress: {}, // { [mediaId]: { progress: 0-100, fileName: string } }
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case SET_INITIAL_STATE:
            return { ...initialState };
        case LOAD_MEDIA: {
            const loadedMedia = payload || [];
            // Set uploadStatus to 'saved' for loaded media (already saved to profile)
            const mediaWithStatus = loadedMedia.map(m => ({
                ...m,
                uploadStatus: m.uploadStatus || 'saved',
            }));
            return { ...state, media: mediaWithStatus, hasUnsavedChanges: false };
        }
        case ADD_MEDIA: {
            const newMedia = Array.isArray(payload) ? payload : [payload];
            // hasUnsavedChanges will be set when uploads complete, not here
            return {
                ...state,
                media: [...state.media, ...newMedia],
            };
        }
        case DELETE_MEDIA: {
            const { ids } = payload;
            const idSet = new Set(ids || []);
            return {
                ...state,
                media: state.media.filter(m => !idSet.has(m.id)),
                selectedMediaIds: state.selectedMediaIds.filter(id => !idSet.has(id)),
                hasUnsavedChanges: true,
            };
        }
        case REORDER_MEDIA:
            return { ...state, media: payload, hasUnsavedChanges: true };
        case SET_FILTER:
            return { ...state, filter: payload };
        case SET_BULK_SELECTION_MODE:
            return {
                ...state,
                bulkSelectionMode: payload,
                selectedMediaIds: payload ? state.selectedMediaIds : [],
            };
        case TOGGLE_MEDIA_SELECTION: {
            const { id } = payload;
            const isSelected = state.selectedMediaIds.includes(id);
            return {
                ...state,
                selectedMediaIds: isSelected
                    ? state.selectedMediaIds.filter(i => i !== id)
                    : [...state.selectedMediaIds, id],
            };
        }
        case SELECT_ALL_MEDIA: {
            const filteredMedia = getFilteredMedia(state.media, state.filter);
            return {
                ...state,
                selectedMediaIds: filteredMedia.map(m => m.id),
            };
        }
        case UNSELECT_ALL_MEDIA:
            return { ...state, selectedMediaIds: [] };
        case SET_GALLERY_VIEW:
            return { ...state, galleryViewIndex: payload };
        case SAVE_REQUEST:
            return { ...state, saveInProgress: true, saveError: null, uploadFailures: [] };
        case SAVE_SUCCESS: {
            // Mark all uploaded items as saved
            return {
                ...state,
                saveInProgress: false,
                hasUnsavedChanges: false,
                media: state.media.map(m =>
                    m.uploadStatus === 'uploaded' ? { ...m, uploadStatus: 'saved' } : m
                ),
            };
        }
        case SAVE_ERROR:
            return { ...state, saveInProgress: false, saveError: payload };
        case LOAD_REQUEST:
            return { ...state, loadInProgress: true, loadError: null };
        case LOAD_SUCCESS:
            return { ...state, loadInProgress: false, loadError: null };
        case LOAD_ERROR:
            return { ...state, loadInProgress: false, loadError: payload };
        // New reducers for upload warnings/failures
        case SET_UPLOAD_WARNING:
            return { ...state, uploadWarning: payload };
        case CLEAR_UPLOAD_WARNING:
            return { ...state, uploadWarning: null };
        case SET_UPLOAD_FAILURES:
            return { ...state, uploadFailures: payload };
        case CLEAR_UPLOAD_FAILURES:
            return { ...state, uploadFailures: [] };
        case SET_STORAGE_USED:
            return { ...state, storageUsed: payload };
        case UPDATE_UPLOAD_PROGRESS:
            return {
                ...state,
                uploadProgress: {
                    ...state.uploadProgress,
                    [payload.mediaId]: { progress: payload.progress, fileName: payload.fileName },
                },
            };
        case CLEAR_UPLOAD_PROGRESS:
            return { ...state, uploadProgress: {} };
        case UPLOAD_START: {
            const { mediaId } = payload;
            return {
                ...state,
                media: state.media.map(m =>
                    m.id === mediaId ? { ...m, uploadStatus: 'uploading' } : m
                ),
            };
        }
        case UPLOAD_SUCCESS: {
            const { mediaId, uploadResult, fileSize } = payload;
            const updatedMedia = state.media.map(m => {
                if (m.id === mediaId) {
                    // Revoke blob URL if it exists
                    if (m.secure_url && m.secure_url.startsWith('blob:')) {
                        URL.revokeObjectURL(m.secure_url);
                    }
                    return {
                        ...m,
                        public_id: uploadResult.public_id,
                        secure_url: uploadResult.secure_url,
                        width: uploadResult.width,
                        height: uploadResult.height,
                        bytes: uploadResult.bytes || fileSize,
                        playback_url: uploadResult.playback_url,
                        duration: uploadResult.duration,
                        uploadStatus: 'uploaded',
                        isStaged: false,
                        file: undefined,
                        fileSize: undefined,
                    };
                }
                return m;
            });

            // Check if there are any unsaved changes (uploaded files, reorders, or deletions)
            const hasUploadedFiles = updatedMedia.some(m => m.uploadStatus === 'uploaded');
            const hasReorderOrDelete = state.hasUnsavedChanges; // Preserve existing state for reorder/delete

            return {
                ...state,
                media: updatedMedia,
                hasUnsavedChanges: hasUploadedFiles || hasReorderOrDelete,
            };
        }
        case UPLOAD_ERROR: {
            const { mediaId, error } = payload;
            return {
                ...state,
                media: state.media.map(m =>
                    m.id === mediaId
                        ? { ...m, uploadStatus: 'failed', uploadError: error }
                        : m
                ),
            };
        }
        case UPDATE_MEDIA_ITEM: {
            const { mediaId, updates } = payload;
            return {
                ...state,
                media: state.media.map(m => (m.id === mediaId ? { ...m, ...updates } : m)),
            };
        }
        case DISMISS_FAILED_UPLOAD: {
            const { mediaId } = payload;
            const item = state.media.find(m => m.id === mediaId);
            // Revoke blob URL if it exists
            if (item && item.secure_url && item.secure_url.startsWith('blob:')) {
                URL.revokeObjectURL(item.secure_url);
            }
            return {
                ...state,
                media: state.media.filter(m => m.id !== mediaId),
            };
        }
        default:
            return state;
    }
}

// Helper to filter media by type
const getFilteredMedia = (media, filter) => {
    if (filter === 'photos') {
        return media.filter(m => m.type === 'photo');
    }
    if (filter === 'videos') {
        return media.filter(m => m.type === 'video');
    }
    return media;
};

// ================ Action creators ================ //

export const setInitialState = () => ({ type: SET_INITIAL_STATE });
export const loadMedia = media => ({ type: LOAD_MEDIA, payload: media });
export const addMedia = media => ({ type: ADD_MEDIA, payload: media });
export const deleteMedia = ids => ({ type: DELETE_MEDIA, payload: { ids } });
export const reorderMedia = media => ({ type: REORDER_MEDIA, payload: media });
export const setFilter = filter => ({ type: SET_FILTER, payload: filter });
export const setBulkSelectionMode = enabled => ({
    type: SET_BULK_SELECTION_MODE,
    payload: enabled,
});
export const toggleMediaSelection = id => ({
    type: TOGGLE_MEDIA_SELECTION,
    payload: { id },
});
export const selectAllMedia = () => ({ type: SELECT_ALL_MEDIA });
export const unselectAllMedia = () => ({ type: UNSELECT_ALL_MEDIA });
export const setGalleryView = index => ({ type: SET_GALLERY_VIEW, payload: index });

export const saveRequest = () => ({ type: SAVE_REQUEST });
export const saveSuccess = () => ({ type: SAVE_SUCCESS });
export const saveError = e => ({ type: SAVE_ERROR, payload: e, error: true });
export const loadRequest = () => ({ type: LOAD_REQUEST });
export const loadSuccess = () => ({ type: LOAD_SUCCESS });
export const loadError = e => ({ type: LOAD_ERROR, payload: e, error: true });

// Action creators for upload warnings/failures
export const setUploadWarning = warning => ({ type: SET_UPLOAD_WARNING, payload: warning });
export const clearUploadWarning = () => ({ type: CLEAR_UPLOAD_WARNING });
export const setUploadFailures = failures => ({ type: SET_UPLOAD_FAILURES, payload: failures });
export const clearUploadFailures = () => ({ type: CLEAR_UPLOAD_FAILURES });
export const setStorageUsed = bytes => ({ type: SET_STORAGE_USED, payload: bytes });

// Action creators for upload progress (chunked uploads)
export const updateUploadProgress = (mediaId, progress, fileName) => ({
    type: UPDATE_UPLOAD_PROGRESS,
    payload: { mediaId, progress, fileName },
});
export const clearUploadProgress = () => ({ type: CLEAR_UPLOAD_PROGRESS });

// Action creators for upload lifecycle
export const uploadStart = mediaId => ({
    type: UPLOAD_START,
    payload: { mediaId },
});
export const uploadSuccess = (mediaId, uploadResult, fileSize) => ({
    type: UPLOAD_SUCCESS,
    payload: { mediaId, uploadResult, fileSize },
});
export const uploadError = (mediaId, error) => ({
    type: UPLOAD_ERROR,
    payload: { mediaId, error },
});
export const updateMediaItem = (mediaId, updates) => ({
    type: UPDATE_MEDIA_ITEM,
    payload: { mediaId, updates },
});
export const dismissFailedUpload = mediaId => ({
    type: DISMISS_FAILED_UPLOAD,
    payload: { mediaId },
});

// ================ Thunks ================ //

/**
 * Validates files for upload with simplified, priority-based rejection.
 * Checks against TOTAL staged media (existing + new) to prevent bypass via multiple uploads.
 * 
 * Priority order (highest = blocks first):
 * 1. Storage quota exceeded - most critical
 * 2. Batch size limit exceeded
 * 3. File count limit exceeded
 * 4. Invalid file type/size - least critical
 * 
 * Returns a single consolidated warning if any files are rejected.
 */
const validateFilesForUpload = (files, existingMedia, storageUsed) => {
    const existingStagedFiles = existingMedia.filter(m => m.isStaged);
    const existingStagedCount = existingStagedFiles.length;
    const existingStagedSize = existingStagedFiles.reduce((sum, m) => sum + (m.fileSize || 0), 0);

    const accepted = [];
    const rejected = [];

    // Calculate remaining capacity
    const remainingFileSlots = MAX_FILES_PER_UPLOAD - existingStagedCount;
    const remainingBatchSize = MAX_UPLOAD_SIZE_PER_BATCH - existingStagedSize;
    const remainingStorageQuota = MAX_STORE_STORAGE - storageUsed;

    // Early exit: no slots available
    if (remainingFileSlots <= 0) {
        return {
            validFiles: [],
            warning: {
                type: 'file_count',
                message: `You've already staged ${existingStagedCount} files. Save or remove some before adding more.`,
            },
        };
    }

    // Early exit: storage quota full
    if (remainingStorageQuota <= 0) {
        return {
            validFiles: [],
            warning: {
                type: 'quota_exceeded',
                message: 'Storage limit reached. Delete some media to free up space.',
            },
        };
    }

    // Track running totals for this batch
    let batchSize = 0;
    let fileCount = 0;

    for (const file of files) {
        // Check file type
        const isValidType = ACCEPTED_PHOTO_TYPES.includes(file.type) || ACCEPTED_VIDEO_TYPES.includes(file.type);
        if (!isValidType) {
            rejected.push({ name: file.name, reason: 'Unsupported format' });
            continue;
        }

        // Check individual file size
        if (file.size > MAX_FILE_SIZE) {
            rejected.push({ name: file.name, reason: `Exceeds ${formatBytes(MAX_FILE_SIZE)} limit` });
            continue;
        }

        // Check file count limit (total staged)
        if (fileCount >= remainingFileSlots) {
            rejected.push({ name: file.name, reason: 'File limit reached' });
            continue;
        }

        // Check batch size limit (total staged)
        if ((batchSize + file.size) > remainingBatchSize) {
            rejected.push({ name: file.name, reason: 'Batch size limit reached' });
            continue;
        }

        // Check storage quota
        if ((storageUsed + batchSize + file.size) > MAX_STORE_STORAGE) {
            rejected.push({ name: file.name, reason: 'Storage quota exceeded' });
            continue;
        }

        // File passes all checks
        accepted.push(file);
        batchSize += file.size;
        fileCount++;
    }

    // Build consolidated warning message if any files were rejected
    let warning = null;
    if (rejected.length > 0) {
        const totalFiles = files.length;
        const acceptedCount = accepted.length;

        // Determine primary reason for rejection (priority-based)
        const reasons = rejected.map(r => r.reason);
        let primaryReason = '';
        let warningType = 'validation';

        if (reasons.includes('Storage quota exceeded')) {
            primaryReason = 'storage limit';
            warningType = 'quota_exceeded';
        } else if (reasons.includes('Batch size limit reached')) {
            primaryReason = 'batch size limit';
            warningType = 'batch_size';
        } else if (reasons.includes('File limit reached')) {
            primaryReason = 'file count limit';
            warningType = 'file_count';
        } else {
            primaryReason = 'invalid files';
            warningType = 'invalid';
        }

        const message = acceptedCount > 0
            ? `${acceptedCount} of ${totalFiles} files added. ${rejected.length} skipped due to ${primaryReason}.`
            : `Could not add files due to ${primaryReason}.`;

        warning = {
            type: warningType,
            message,
            skippedFiles: rejected.map(r => `${r.name} (${r.reason})`),
        };
    }

    return { validFiles: accepted, warning };
};

// Maximum concurrent uploads
const MAX_CONCURRENT_UPLOADS = 3;

// Upload a single media item immediately
export const uploadMediaItem = (mediaItem, abortController) => async (dispatch, getState) => {
    const { file, id: mediaId, fileSize } = mediaItem;
    const isVideo = file.type.startsWith('video/');

    try {
        dispatch(uploadStart(mediaId));
        dispatch(updateUploadProgress(mediaId, 0.1, file.name));

        let uploadResult;
        if (isVideo) {
            // Use chunked upload for videos (with progress tracking and retry)
            uploadResult = await UploadVideoChunked({
                file,
                id: mediaId,
                folder: 'store-media',
                onProgress: progress => {
                    // Check if aborted
                    if (abortController?.signal.aborted) {
                        throw new Error('Upload cancelled');
                    }
                    dispatch(updateUploadProgress(mediaId, progress, file.name));
                },
            });
        } else {
            // Use single upload for images with retry logic
            let retryCount = 0;
            const MAX_RETRIES = 2;
            let lastError;

            while (retryCount <= MAX_RETRIES) {
                try {
                    // Check if aborted
                    if (abortController?.signal.aborted) {
                        throw new Error('Upload cancelled');
                    }

                    uploadResult = await UploadImage({
                        file,
                        id: mediaId,
                        folder: 'store-media',
                    });
                    dispatch(updateUploadProgress(mediaId, 100, file.name));
                    break; // Success, exit retry loop
                } catch (error) {
                    lastError = error;
                    retryCount++;
                    if (retryCount <= MAX_RETRIES) {
                        // Exponential backoff: 1s, 2s
                        const delay = 1000 * Math.pow(2, retryCount - 1);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }

            if (!uploadResult) {
                throw lastError || new Error('Upload failed after retries');
            }
        }

        // Check if aborted after upload completes
        if (abortController?.signal.aborted) {
            // Try to delete from Cloudinary if uploaded
            if (uploadResult?.public_id) {
                try {
                    await DeleteMedia({
                        publicId: uploadResult.public_id,
                        resourceType: isVideo ? 'video' : 'image',
                    });
                } catch (deleteError) {
                    console.error('Failed to delete cancelled upload:', deleteError);
                }
            }
            throw new Error('Upload cancelled');
        }

        dispatch(uploadSuccess(mediaId, uploadResult, fileSize));

        // ── SEO tag generation — non-blocking, non-critical ───────────────────────────
        // Runs after upload succeeds. Failure here must never affect upload state.
        const generateAndAttachSeoTags = async () => {
            try {
                const state = getState();
                const userPublicData = state.user.currentUser?.attributes?.profile?.publicData || {};
                const storeName = userPublicData.storeName || '';
                const city =
                    userPublicData.storeAddress?.city ||
                    userPublicData.city ||
                    '';

                // Build AI-ready image URL:
                // Video → first-frame JPEG thumbnail via Cloudinary transformation
                // Image → use secure_url directly
                const imageUrlForAI = isVideo
                    ? `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD}/video/upload/so_0,w_800,f_jpg/${uploadResult.public_id}.jpg`
                    : uploadResult.secure_url;

                const { data: seoTags } = await generateGalleryImageSeoTags({
                    imageUrl: imageUrlForAI,
                    storeName,
                    city,
                    mediaType: isVideo ? 'video' : 'image',
                });

                // Attach to Redux state so the tags are available immediately in the UI
                dispatch(updateMediaItem(mediaId, { seoTags }));

                // Write to Cloudinary asset context (fire-and-forget)
                updateCloudinaryContext({
                    publicId: uploadResult.public_id,
                    context: seoTags,
                    resourceType: isVideo ? 'video' : 'image',
                }).catch(err => console.error('Cloudinary context update failed:', err));

            } catch (err) {
                // SEO tags are non-critical — upload already succeeded
                console.warn('Gallery SEO tag generation failed (non-critical):', err.message);
            }
        };

        generateAndAttachSeoTags(); // intentionally not awaited
        // ─────────────────────────────────────────────────────────────────────────────

        return { success: true, mediaId };
    } catch (error) {
        // Don't set error if it was cancelled
        if (error.message !== 'Upload cancelled') {
            const errorMessage = error.message || 'Upload failed';
            dispatch(uploadError(mediaId, errorMessage));
        }
        throw error;
    }
};

// Stage media files locally and trigger immediate uploads
export const stageMedia = files => async (dispatch, getState) => {
    try {
        const state = getState();
        const { media, storageUsed } = state.MediaLibraryPage;

        // Clear previous warnings
        dispatch(clearUploadWarning());

        // Validate files - check against already uploaded media, not staged
        const existingUploadedMedia = media.filter(m => m.uploadStatus === 'uploaded');
        const existingUploadedCount = existingUploadedMedia.length;
        const existingUploadedSize = existingUploadedMedia.reduce((sum, m) => sum + (m.bytes || 0), 0);

        // Calculate remaining capacity
        const remainingFileSlots = MAX_FILES_PER_UPLOAD - existingUploadedCount;
        const remainingStorageQuota = MAX_STORE_STORAGE - storageUsed;

        const accepted = [];
        const rejected = [];

        // Early exit: no slots available
        if (remainingFileSlots <= 0) {
            dispatch(setUploadWarning({
                type: 'file_count',
                message: `You've already uploaded ${existingUploadedCount} files. Save or remove some before adding more.`,
            }));
            return [];
        }

        // Early exit: storage quota full
        if (remainingStorageQuota <= 0) {
            dispatch(setUploadWarning({
                type: 'quota_exceeded',
                message: 'Storage limit reached. Delete some media to free up space.',
            }));
            return [];
        }

        // Validate each file
        let batchSize = 0;
        let fileCount = 0;

        for (const file of Array.from(files)) {
            // Check file type
            const isValidType = ACCEPTED_PHOTO_TYPES.includes(file.type) || ACCEPTED_VIDEO_TYPES.includes(file.type);
            if (!isValidType) {
                rejected.push({ name: file.name, reason: 'Unsupported format' });
                continue;
            }

            // Check individual file size
            if (file.size > MAX_FILE_SIZE) {
                rejected.push({ name: file.name, reason: `Exceeds ${formatBytes(MAX_FILE_SIZE)} limit` });
                continue;
            }

            // Check file count limit
            if (fileCount >= remainingFileSlots) {
                rejected.push({ name: file.name, reason: 'File limit reached' });
                continue;
            }

            // Check storage quota
            if ((storageUsed + existingUploadedSize + batchSize + file.size) > MAX_STORE_STORAGE) {
                rejected.push({ name: file.name, reason: 'Storage quota exceeded' });
                continue;
            }

            // File passes all checks
            accepted.push(file);
            batchSize += file.size;
            fileCount++;
        }

        // Build warning message if any files were rejected
        if (rejected.length > 0) {
            const totalFiles = files.length;
            const acceptedCount = accepted.length;

            const reasons = rejected.map(r => r.reason);
            let primaryReason = '';
            let warningType = 'validation';

            if (reasons.includes('Storage quota exceeded')) {
                primaryReason = 'storage limit';
                warningType = 'quota_exceeded';
            } else if (reasons.includes('File limit reached')) {
                primaryReason = 'file count limit';
                warningType = 'file_count';
            } else {
                primaryReason = 'invalid files';
                warningType = 'invalid';
            }

            const message = acceptedCount > 0
                ? `${acceptedCount} of ${totalFiles} files added. ${rejected.length} skipped due to ${primaryReason}.`
                : `Could not add files due to ${primaryReason}.`;

            dispatch(setUploadWarning({
                type: warningType,
                message,
                skippedFiles: rejected.map(r => `${r.name} (${r.reason})`),
            }));
        }

        if (accepted.length === 0) {
            return [];
        }

        // Create staged media items
        const stagedMedia = accepted.map((file, index) => {
            const isVideo = file.type.startsWith('video/');
            const mediaId = uuidv4();
            const previewUrl = URL.createObjectURL(file);

            return {
                id: mediaId,
                type: isVideo ? 'video' : 'photo',
                file,
                fileSize: file.size,
                secure_url: previewUrl,
                uploadStatus: 'pending',
                order: media.length + index,
                createdAt: new Date().toISOString(),
            };
        });

        dispatch(addMedia(stagedMedia));

        // Start uploads immediately with concurrency limit
        // Uploads will be handled asynchronously, component will track them
        stagedMedia.forEach(mediaItem => {
            // Dispatch upload asynchronously (don't await to allow concurrent uploads)
            dispatch(uploadMediaItem(mediaItem, null)).catch(error => {
                // Error already handled in uploadMediaItem
                console.error(`Upload failed for ${mediaItem.file.name}:`, error);
            });
        });

        return stagedMedia;
    } catch (error) {
        console.error('Error staging media:', error);
        return [];
    }
};

// Save media order to user profile - only saves order/deletions, uploads already done
export const saveMedia = () => async (dispatch, getState, sdk) => {
    dispatch(saveRequest());
    dispatch(clearUploadProgress()); // Clear any previous progress

    try {
        const state = getState();
        const user = ensureCurrentUser(state.user.currentUser);
        let media = state.MediaLibraryPage.media;

        // Get previously saved media to identify deletions
        const previouslySavedMedia = user.attributes?.profile?.publicData?.storeMedia || [];
        const currentMediaIds = new Set(media.map(m => m.id));
        const deletedMedia = previouslySavedMedia.filter(
            prev => !currentMediaIds.has(prev.id) && prev.public_id
        );

        // Delete removed media from Cloudinary
        const deleteErrors = [];
        if (deletedMedia.length > 0) {
            const deletePromises = deletedMedia.map(async mediaItem => {
                try {
                    await DeleteMedia({
                        publicId: mediaItem.public_id,
                        resourceType: mediaItem.type === 'video' ? 'video' : 'image',
                    });
                } catch (error) {
                    console.error(`Failed to delete ${mediaItem.public_id} from Cloudinary:`, error);
                    deleteErrors.push(mediaItem.public_id);
                }
            });
            await Promise.all(deletePromises);
        }

        // Filter out failed uploads and only save successfully uploaded or already saved media
        // Also filter out items that are still uploading or pending
        const mediaToSave = media
            .filter(m => {
                // Only include successfully uploaded or already saved items
                return m.uploadStatus === 'uploaded' || m.uploadStatus === 'saved' || (!m.uploadStatus && m.public_id);
            })
            .map(({ file, isStaged, fileSize, uploadStatus, uploadError, ...rest }) => {
                // Mark as saved
                return { ...rest, uploadStatus: 'saved' };
            });

        // Check if there are any items to save
        if (mediaToSave.length === 0 && deletedMedia.length === 0) {
            // Nothing to save
            dispatch(saveSuccess());
            return { success: true };
        }

        // Calculate total storage used
        const totalStorageUsed = mediaToSave.reduce((sum, m) => sum + (m.bytes || 0), 0);

        const publicData = {
            storeMedia: mediaToSave,
            storeMediaMeta: {
                totalBytes: totalStorageUsed,
                lastUpdated: new Date().toISOString(),
            },
        };

        const queryParams = {
            expand: true,
            include: ['profileImage'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
        };

        const response = await sdk.currentUser.updateProfile({ publicData }, queryParams);
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
            throw new Error('Unable to save changes. Please try again.');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        dispatch(saveSuccess());
        dispatch(clearUploadProgress()); // Clear progress after successful save
        // Update state with saved media and storage info
        dispatch(loadMedia(mediaToSave));
        dispatch(setStorageUsed(totalStorageUsed));

        // If there were delete errors, warn but don't fail
        if (deleteErrors.length > 0) {
            console.warn(`Some media could not be deleted from Cloudinary: ${deleteErrors.join(', ')}`);
        }

        return { success: true };
    } catch (e) {
        dispatch(clearUploadProgress()); // Clear progress on error too
        // Create user-friendly error message
        let errorMessage = 'Unable to save changes. Please check your connection and try again.';
        if (e.message) {
            // Use the error message if it's already user-friendly
            if (e.message.includes('Unable to') || e.message.includes('Some files') || e.message.includes('Please')) {
                errorMessage = e.message;
            } else if (e.message.includes('network') || e.message.includes('Network')) {
                errorMessage = 'Unable to save changes. Please check your internet connection and try again.';
            }
        }
        return dispatch(saveError(new Error(errorMessage)));
    }
};

// Delete media items - revoke blob URLs and cancel uploads if needed
export const deleteMediaItems = ids => (dispatch, getState) => {
    // Revoke preview URLs for items being deleted
    const { media } = getState().MediaLibraryPage;
    ids.forEach(id => {
        const item = media.find(m => m.id === id);
        if (item && item.secure_url && item.secure_url.startsWith('blob:')) {
            URL.revokeObjectURL(item.secure_url);
        }
        // Note: Upload cancellation will be handled by component cleanup
    });

    dispatch(deleteMedia(ids));
};

// Retry failed upload
export const retryUpload = mediaId => (dispatch, getState) => {
    const state = getState();
    const mediaItem = state.MediaLibraryPage.media.find(m => m.id === mediaId);

    if (!mediaItem || !mediaItem.file) {
        console.error('Cannot retry: media item not found or file missing');
        return;
    }

    if (mediaItem.uploadStatus !== 'failed') {
        console.error('Cannot retry: media item is not in failed state');
        return;
    }

    // Reset status and retry
    dispatch(updateMediaItem(mediaId, { uploadStatus: 'pending', uploadError: undefined }));
    return dispatch(uploadMediaItem(mediaItem, null));
};

// Reorder media items
export const reorderMediaItems = (sourceIndex, destinationIndex) => (dispatch, getState) => {
    const { media } = getState().MediaLibraryPage;
    const newMedia = Array.from(media);
    const [moved] = newMedia.splice(sourceIndex, 1);
    newMedia.splice(destinationIndex, 0, moved);
    // Update order values
    const reorderedMedia = newMedia.map((item, index) => ({
        ...item,
        order: index,
    }));
    dispatch(reorderMedia(reorderedMedia));
};

// Load media from user profile
export const loadData = () => async (dispatch, getState, sdk) => {
    dispatch(loadRequest());
    let { currentUser } = getState().user;

    if (!currentUser) {
        try {
            const cUser = await sdk.currentUser.show();
            currentUser = cUser.data.data;
        } catch (error) {
            // User not authenticated
        }
    }

    try {
        const user = ensureCurrentUser(currentUser);
        const publicData = user.attributes?.profile?.publicData || {};
        const media = publicData.storeMedia || [];
        const storeMediaMeta = publicData.storeMediaMeta || {};

        // Ensure order is set for all media items
        const mediaWithOrder = media.map((m, index) => ({
            ...m,
            order: m.order !== undefined ? m.order : index,
        }));
        // Sort by order
        mediaWithOrder.sort((a, b) => (a.order || 0) - (b.order || 0));

        dispatch(loadMedia(mediaWithOrder));

        // Set storage used from meta or calculate from media
        const storageUsed = storeMediaMeta.totalBytes || media.reduce((sum, m) => sum + (m.bytes || 0), 0);
        dispatch(setStorageUsed(storageUsed));

        dispatch(loadSuccess());
    } catch (e) {
        dispatch(loadError(storableError(e)));
    }
    return Promise.resolve();
};
